import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FaSearch } from "react-icons/fa";
import { RxReload } from "react-icons/rx";
import AdminNavbar from "../../../components/CommonComponents/AdminNavbar";
import Response_Processing from "../../../layout/Others/Response_Processing";
import { useMyContext } from "../../../components/Provider/CommonProvider";

// Partner Content Overview — aggregated dashboard of pending content per partner.
// Replaces the per-partner-click workflow (130+ partners on disbeaute, unworkable to
// click through one by one). Aaron asked for this on 2026-06-17 (Basecamp thread
// "Partner Content Approval"). Clicking "View" drills into the existing approval page
// pre-filtered to that partner + source type.
function PartnerContentOverview({ ToggleHideButton, accessToken }) {
    const navigate = useNavigate();
    const BaseUrl = process.env.REACT_APP_Baseurl;
    const overviewApi = `${BaseUrl}/admin/partner/pending-content-overview`;
    const headers = {
        "Content-Type": "application/json",

        Authorization: "Bearer " + (accessToken || localStorage.getItem("access_token")),
        Accept: "application/json",
    };

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [totals, setTotals] = useState({ partners: 0, items: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(overviewApi, { headers });
                if (cancelled) return;
                if (res?.data?.status) {
                    setRows(res.data.data || []);
                    setTotals({
                        partners: res.data.total_partners_with_pending || 0,
                        items: res.data.total_pending_items || 0,
                    });
                } else {
                    toast.error(res?.data?.message || "Unable to load overview");
                }
            } catch (e) {
                toast.error(e?.response?.data?.message || "Unable to load overview");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [refresh]);

    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const urlPartnerId = query.get('partner_id');
    const urlSourceType = query.get('source_type');
    const filteredRows = useMemo(() => {
        let data = rows;
        if (urlPartnerId) {
            data = data.filter(r => String(r.partner_id) === String(urlPartnerId));
        }
        if (urlSourceType) {
            const type = Number(urlSourceType);
            // Map source_type to the corresponding field in row
            const fieldMap = { 1: 'videos', 2: 'livestreams', 3: 'series', 4: 'episodes', 5: 'audios' };
            const field = fieldMap[type];
            if (field) {
                data = data.filter(r => r[field] && r[field] > 0);
            }
        }
        return data;
    }, [rows, urlPartnerId, urlSourceType]);

    const filtered = useMemo(() => {
        const data = filteredRows;
        if (!search) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.partner_name || "").toLowerCase().includes(q) ||
            (r.partner_email || "").toLowerCase().includes(q)
        );
    }, [filteredRows, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, urlPartnerId, urlSourceType]);

    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filtered.slice(start, start + itemsPerPage);
    }, [filtered, currentPage, itemsPerPage]);

    // Drill into the existing approval page pre-filtered for this partner + first
    // source type that has pending items.
    const openPartner = (row) => {
        navigate(`/partner-content?partner_id=${row.partner_id}&source_type=all`);
    };

    return (
        <div className="App">
            {loading && <Response_Processing />}
            <AdminNavbar heading="Partner Content Overview" totalDataLength={filtered.length} ToggleHideButton={ToggleHideButton} />
            <div className="m-3 p-3 bg-white rounded-3 shadow-sm">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: 12 }}>
                    <div>
                        <h5 className="mb-1">Pending Content by Partner</h5>
                        <small className="text-muted">
                            {totals.partners} partner{totals.partners === 1 ? "" : "s"} with pending uploads &middot; {totals.items} item{totals.items === 1 ? "" : "s"} total
                        </small>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <div className="position-relative">
                            <FaSearch style={{ position: "absolute", top: 12, left: 12, color: "#666" }} />
                            <input
                                type="text"
                                placeholder="Search partner"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="form-control"
                                style={{ paddingLeft: 36, minWidth: 240 }}
                            />
                        </div>
                        <button className="btn btn-outline-secondary" onClick={() => setRefresh(r => r + 1)} title="Refresh">
                            <RxReload />
                        </button>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Partner</th>
                                <th className="text-center">Videos</th>
                                <th className="text-center">Live</th>
                                <th className="text-center">Series</th>
                                <th className="text-center">Episodes</th>
                                <th className="text-center">Audios</th>
                                <th className="text-center">Total</th>
                                <th className="text-center">Approved</th>
                                <th className="text-center">Disapproved</th>
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={9} className="text-center py-4 text-muted">
                                        {rows.length === 0 ? "No partners with pending content. All caught up." : "No partners match this search."}
                                    </td>
                                </tr>
                            ) : (
                                paginatedRows.map((r) => (
                                    <tr key={r.partner_id} style={{ cursor: "pointer" }} onClick={() => openPartner(r)}>
                                        <td>
                                            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                                {r.partner_avatar && (
                                                    <img src={r.partner_avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                                                )}
                                                <div>
                                                    <div className="fw-semibold">{r.partner_name || "Unnamed"}</div>
                                                    <small className="text-muted">{r.partner_email}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-center">{r.videos > 0 ? <span className="badge bg-primary">{r.videos}</span> : <span className="text-muted">-</span>}</td>
                                        <td className="text-center">{r.livestreams > 0 ? <span className="badge bg-primary">{r.livestreams}</span> : <span className="text-muted">-</span>}</td>
                                        <td className="text-center">{r.series > 0 ? <span className="badge bg-primary">{r.series}</span> : <span className="text-muted">-</span>}</td>
                                        <td className="text-center">{r.episodes > 0 ? <span className="badge bg-primary">{r.episodes}</span> : <span className="text-muted">-</span>}</td>
                                        <td className="text-center">{r.audios > 0 ? <span className="badge bg-primary">{r.audios}</span> : <span className="text-muted">-</span>}</td>
                                        <td className="text-center fw-bold"><span className="badge bg-warning text-dark">{r.total_pending}</span></td>
                                        <td className="text-center text-success fw-bold">{r.total_approved || <span className="text-muted">-</span>}</td>
                                        <td className="text-center text-danger fw-bold">{r.total_disapproved || <span className="text-muted">-</span>}</td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); openPartner(r); }}>
                                                Review
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {filtered.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap" style={{ gap: 15 }}>
                        <small className="text-muted">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} entries
                        </small>
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                            <select
                                className="form-select form-select-sm"
                                value={itemsPerPage}
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: "80px", cursor: "pointer" }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <div className="btn-group btn-group-sm">
                                <button
                                    className="btn btn-outline-secondary"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >
                                    Prev
                                </button>
                                <span className="btn btn-outline-secondary disabled theme-text-color" style={{ opacity: 1, backgroundColor: "transparent" }}>
                                    {currentPage} / {Math.ceil(filtered.length / itemsPerPage)}
                                </span>
                                <button
                                    className="btn btn-outline-secondary"
                                    disabled={currentPage * itemsPerPage >= filtered.length}
                                    onClick={() => setCurrentPage(p => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PartnerContentOverview;
